// CompanyUnitDialog - MODULE
// ***********************************************************************************************************************
import { Checkbox, FormControlLabel, Grid, TextField } from "@mui/material";
import * as React from "react";
import { Base } from "ui/Scripts/source/framework/base";
import { ApplicationPermissionType, ConfirmationDialogResult, EnumHelper } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../../framework/appUtils";
import * as store from "../../../../framework/customStore";
import { SaveDataObj } from "../../../../framework/saveData";
import { ISessionState } from "../../../../models/session/sessionState";
import * as storeActions from "../../../../models/store/storeActions";
import { IUnitModuleNode } from "../../../../models/unit/unitModuleNode";
import { IUnitModuleNodeUsage, UnitModuleNodeUsage } from "../../../../models/unit/unitModuleNodeUsage";
import { Dialog } from "../../../framework/Dialog/Dialog";
import { FormField } from "../../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";

//CompanyUnitDialog
export interface ICompanyUnitDialogProp {
    sessionState: ISessionState;
    classes?: string;
    unitModuleNode: IUnitModuleNode;
    isNew: boolean;
    isReadOnly?: boolean;
    canEditWhistleBlowSettings: boolean;
    onOk: (name: string, comment: string, usages: IUnitModuleNodeUsage[]) => void;
    onCancel: () => void;
}

export interface ICompanyUnitDialogState {
    name: string;
    comment: string;
    usages: IUnitModuleNodeUsage[];
}

export class CompanyUnitDialog extends React.Component<ICompanyUnitDialogProp, ICompanyUnitDialogState> {
    private orgStateHash: string = "";

    constructor(props: ICompanyUnitDialogProp) {
        super(props);
        const unitModuleNode = props.unitModuleNode;
        this.state = {
            name: unitModuleNode.name,
            comment: unitModuleNode.comment,
            usages: Base.getTypedArray(unitModuleNode.usages, UnitModuleNodeUsage)
        };
        const saveData = this.getSaveDataFromState(props, this.state);
        this.orgStateHash = saveData.hash;
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "name") {
            this.setState({ name: value });
        } else if (name === "comment") {
            this.setState({ comment: value });
        }
    };

    handleChangeUsage = (applicationPermissionType: ApplicationPermissionType, value: boolean) => {
        const state = this.state;
        const usages = Base.getTypedArray(state.usages, UnitModuleNodeUsage);
        const usage = usages.find(i => EnumHelper.isEqual(i.applicationPermissionType, applicationPermissionType));
        if (!usage) return;
        usage.inUse = value;
        this.setState({
            usages
        });
    };

    getSaveDataFromState = (props: ICompanyUnitDialogProp, state: ICompanyUnitDialogState): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("name", state.name);
        result.append("comment", state.comment);
        result.append("usages", state.usages);
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const props = this.props;
        const state = this.state;
        return new Promise<boolean>((resolve) => {
            if (!state.name) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.UnitNameMustBeDefined));
                return resolve(false);
            }
            const siblingNames = props.unitModuleNode.getSiblingNames(true);
            const lowName = state.name.toLowerCase();
            if (siblingNames.findIndex(i => i === lowName) > -1) {
                store.customStore.dispatch(storeActions.showErrorMessage(Translations.UnitCodeIsAlreadyInUse));
                return resolve(false);
            }
            return resolve(true);
        });
    };

    save = async () => {
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return;
        this.props.onOk(state.name, state.comment, state.usages);
    };

    handleSave = () => {
        this.save();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel(this.getSaveDataFromState(props, state).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.save();
        } else {
            props.onCancel();
        }
    };

    render() {
        const props = this.props;
        const state = this.state;
        const isRoot = props.unitModuleNode.getIsRoot();
        const usageIsDisabled = !props.unitModuleNode.getParentIsRoot();
        return (
            <Dialog
                id="company-unit-dialog"
                maxWidth="sm"
                title={props.unitModuleNode.isNew() ? Translations.AddUnit + " (" + props.unitModuleNode.parent.name + ")" : Translations.EditUnit + ": " + state.name}
                body={
                    <>
                        <Grid container>
                            <Grid item sm={12}>
                                <FormField title={Translations.Name} required margin={0}>
                                    <TextField type="text" name="name" title={Translations.Name} inputProps={{ maxLength: 50 }} disabled={isRoot || props.isReadOnly} value={state.name} onChange={this.handleChange} size="small" fullWidth />
                                </FormField>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item sm={12}>
                                <FormField title={Translations.Usage}>
                                    <Grid container>
                                        {state.usages.filter(i => i.visible).map((usage) => usage.enabled &&
                                            <Grid item sm={6} key={usage.getTitle(props.sessionState.isSuperAdmin, props.sessionState.canUseRecords).replace(" ", "")}>
                                                <FormControlLabel
                                                    label={usage.getTitle(props.sessionState.isSuperAdmin, props.sessionState.canUseRecords)}
                                                    control={
                                                        <Checkbox
                                                            checked={usage.inUse}
                                                            disabled={usageIsDisabled || props.isReadOnly || usage.applicationPermissionType === ApplicationPermissionType.WhistleBlow && !props.canEditWhistleBlowSettings}
                                                            onChange={(_e, checked: boolean) => this.handleChangeUsage(usage.applicationPermissionType, checked)}
                                                        />
                                                    }
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </FormField>
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item sm={12}>
                                <FormField title={Translations.Description}>
                                    <TextField name="comment" title={Translations.Description} inputProps={{ maxLength: 1000 }} disabled={props.isReadOnly} value={state.comment} onChange={this.handleChange} multiline rows={5} />
                                </FormField>
                            </Grid>
                        </Grid>
                    </>
                }
                buttons={[
                    { title: props.isNew ? Translations.Cancel : Translations.Close, variant: "outlined", onClick: this.handleCancel },
                    { title: Translations.OK, variant: "confirmation", onClick: !props.isReadOnly ? this.handleSave : null },
                ]}
                onClose={this.handleCancel}/>
        );
    }
}